import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment, { Moment } from "moment-timezone";

import * as advanceBookingRequestService from "../../../services/advanceBookingRequestService";
import useTablePagination from "../../../hooks/useTablePagination";
import useValue from "../../../hooks/useValue";
import { CustomNotification } from "../../../util/CustomNotification";
import { ABRStatusConstants, PaymentGatewayConstant } from "../../../constants/DatabaseConstants/ABRConstants";
import { DefaultPagination } from "../../../constants/generalConstants";
import { ErrorMessageConstants } from "../../../constants/MessageConstants/ErrorMessageConstants";

import type { RootState } from "../../../appRedux/store";
import type { FormattedAdvanceBookingReportRecord, paramsObject } from "../../../shared/interface/ABRReport.interface";

export const useAdvanceBookingRequest = () => {
  /* STATE */
  const [abrList, setABRList] = useState<FormattedAdvanceBookingReportRecord[]>([]);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [searchedText, setSearchedText] = useState("");
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [selectedCompletionStatus, setSelectedCompletionStatus] = useState<string>(ABRStatusConstants.COMPLETED);
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState<PaymentGatewayConstant | null>(null);
  const [reFetch, setRefetch] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  /* HOOK */
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);
  const { masterFilters } = useSelector((state: RootState) => state.common);
  const { value: loading, handleEnableValue: startLoading, handleDisableValue: stopLoading } = useValue(false);

  /* HANDLERS */
  let startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleDateChange = (date: Moment | null) => {
    handleResetPagination();
    setSelectedDate(date ? date.format("YYYY-MM-DD") : "");
  };

  const handleChangeStatusFilter = (value: string) => {
    handleResetPagination();
    setSelectedCompletionStatus(value);
  };

  const handleChangePaymentGateway = (value: PaymentGatewayConstant) => {
    handleResetPagination();
    setSelectedPaymentGateway(value);
  };

  const handleRefreshList = () => {
    setRefetch(true);
  };

  const handleChangeOnSearch = (value: string) => {
    const trimmedValue = value?.trimStart();
    setSearchedText(trimmedValue);
    handleResetPagination();
    if (!value) setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    const trimmedValue = value?.trimStart();
    setSearchedText(trimmedValue);
    handleResetPagination();
    setRefetch(true);
  };

  const fetchABRList = async () => {
    try {
      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      if (!selectedDate) {
        return CustomNotification.showErrorMessage("Please select a date.");
      }

      startLoading();

      let params: paramsObject & { paymentGateway?: PaymentGatewayConstant } = {
        date: moment(selectedDate).startOf("day").format("YYYY-MM-DD"),
        page: pagination?.current || DefaultPagination.PAGE,
        pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId
      };

      if (searchedText) params["searchQuery"] = searchedText;
      if (selectedCompletionStatus) params["status"] = selectedCompletionStatus;
      if (selectedPaymentGateway) params["paymentGateway"] = selectedPaymentGateway;

      const response = await advanceBookingRequestService.getABRReportList(params);

      const formatted = response?.data?.rows?.map((item, index: number) => ({
        ...item,
        parking_amount: item.amount.parking_amount,
        valet_parking_amount: item.amount.valet_parking_amount,
        penalty_amount: item.amount.penalty_amount,
        overdue_amount: item.amount.overdue_amount,
        payable_amount: item.amount.payable_amount,
        sr_no: startIndex + index + 1
      }));

      handleChangeTotalSize(response?.data?.count || 0);
      setABRList(formatted);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
      handleChangeTotalSize(0);
      setABRList([]);
    } finally {
      setRefetch(false);
      stopLoading();
    }
  };

  const handleModalOpen = (recordId: number) => {
    setSelectedRecordId(recordId);
    setIsModalVisible(true);
  };

  const handleOnClose = () => {
    setIsModalVisible(false);
    setSelectedRecordId(null);
  };

  /* EFFECT */
  useEffect(() => {
    setRefetch(true);
  }, [pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId, selectedDate, selectedCompletionStatus, selectedPaymentGateway]);

  useEffect(() => {
    if (reFetch) fetchABRList();
  }, [reFetch]);

  return {
    abrList,
    loading,
    pagination,
    handleChangePagination,
    setSelectedDate,
    selectedDate,
    fetchABRList,
    handleDateChange,
    handleRefreshList,
    isModalVisible,
    selectedRecordId,
    handleChangeSearch,
    searchedText,
    handleOnClose,
    handleModalOpen,
    selectedCompletionStatus,
    handleChangeStatusFilter,
    handleChangeOnSearch,
    handleChangePaymentGateway,
    selectedPaymentGateway
  };
};
